import { useEffect, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { useGetEnvironmentVariableByServiceTypeQuery } from '@/services/EnvironmentVariablesService';
import { ExternalServiceType } from '@/types/environment-variables.types';

/**
 * Resolves Mapbox public token from external-services.
 * Prefers admin (VITE) type 30; falls back to consumer (NEXT_PUBLIC) type 31
 * when 30 is missing/empty (common on staging).
 */
export function useMapboxAccessToken() {
  const viteQuery = useGetEnvironmentVariableByServiceTypeQuery(
    ExternalServiceType.VITE_PUBLIC_MAPBOX_ACCESS_TOKEN
  );
  const nextQuery = useGetEnvironmentVariableByServiceTypeQuery(
    ExternalServiceType.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN
  );

  const token = useMemo(() => {
    const viteKey = viteQuery.data?.secret_key?.trim();
    if (viteKey) return viteKey;
    const nextKey = nextQuery.data?.secret_key?.trim();
    if (nextKey) return nextKey;
    return '';
  }, [viteQuery.data?.secret_key, nextQuery.data?.secret_key]);

  const isLoading = viteQuery.isLoading || nextQuery.isLoading;
  const bothFailed = Boolean(viteQuery.isError && nextQuery.isError);
  const error =
    !token && !isLoading
      ? viteQuery.error || nextQuery.error || (bothFailed ? viteQuery.error : undefined)
      : undefined;

  useEffect(() => {
    if (!token) return;
    mapboxgl.accessToken = token;
  }, [token]);

  useEffect(() => {
    if (!error) return;
    console.error('Failed to load Mapbox access token:', error);
  }, [error]);

  return {
    token,
    isMapboxReady: Boolean(token),
    isLoading,
    error,
  };
}
